/** Detalle de presupuesto: lectura + acciones + partidas + edición comercial + versiones. */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';
import { FileCapture } from '@/components/FileCapture';
import { QuantityStepper } from '@/components/QuantityStepper';
import { SectionCard } from '@/components/SectionCard';
import { StatusBadge } from '@/components/StatusBadge';
import { StatusHistoryCard } from '@/components/StatusHistoryCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ServerActionRunner } from '@/components/ServerActionRunner';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useAddBudgetItemMutation,
  useBudgetActionMutation,
  useBudgetAdjustmentMutations,
  useBudgetTotalsMutations,
  useDeleteBudgetItemMutation,
  useGenerateQuotationMutation,
  useMobileBudgetAttachmentsQuery,
  useMobileBudgetContextQuery,
  useMobileBudgetItemsQuery,
  useMobileBudgetQuotationsQuery,
  useMobileBudgetQuery,
  useMobileBudgetVersionsQuery,
  useMobileCatalogQuery,
  useUpdateBudgetItemMutation,
  type CatalogGroup,
  useUploadBudgetAttachmentsMutation,
} from '@/hooks/useMobileQuotations';
import { useMobileWorkflowActions } from '@/hooks/useMobileWorkflowActions';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { useMonetaryPrecision } from '@/state/MonetaryPrecisionContext';
import type { CatalogItemDto, MobileBudgetItemDto } from '@/services/commercialService';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { countDecimals, formatPercent } from '@/utils/format';
import { hapticSuccess } from '@/utils/haptic';

/** Misma regla que la web: lo congela la decisión (approved), no la etapa. */
function isBudgetEditable(status: string | undefined): boolean {
  return status !== 'approved' && status !== 'cancelled' && status !== 'superseded';
}

function toNumber(value: number | string | null | undefined): number | undefined {

  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function itemTitle(item: MobileBudgetItemDto): string {
  return item.description || item.master_item?.name || 'Partida';
}

type BuildResult = { ok: false; error: string } | { ok: true; payload: Record<string, unknown> };

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useMobileAuth();
  const precision = useMonetaryPrecision();
  const canViewTechnical = hasPermission('budget_items.view');
  const canViewCommercial = hasPermission('budgets.commercial_items.view');
  const budgetId = id ?? null;
  const budgetQuery = useMobileBudgetQuery(budgetId);
  const contextQuery = useMobileBudgetContextQuery(budgetId);
  const itemsQuery = useMobileBudgetItemsQuery(budgetId);
  const attachmentsQuery = useMobileBudgetAttachmentsQuery(budgetId);
  const uploadAttachmentsMutation = useUploadBudgetAttachmentsMutation();
  const actionMutation = useBudgetActionMutation(budgetId ?? '');
  const generateMutation = useGenerateQuotationMutation(budgetId ?? '');
  const addItemMutation = useAddBudgetItemMutation(budgetId ?? '');
  const updateItemMutation = useUpdateBudgetItemMutation(budgetId ?? '');
  const deleteItemMutation = useDeleteBudgetItemMutation(budgetId ?? '');
  const adjustments = useBudgetAdjustmentMutations(budgetId ?? '');
  const totals = useBudgetTotalsMutations(budgetId ?? '');

  const [addSearch, setAddSearch] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [lineGroup, setLineGroup] = useState<CatalogGroup>('sale');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('0');
  const [editMargin, setEditMargin] = useState('');
  const [deletingItem, setDeletingItem] = useState<MobileBudgetItemDto | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'discount' | 'surcharge'>('discount');
  const [adjustmentMode, setAdjustmentMode] = useState<'amount' | 'percent'>('amount');
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [adjustmentJustification, setAdjustmentJustification] = useState('');
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
  const [simulationPreview, setSimulationPreview] = useState<string | null>(null);
  const adjustmentSimulationKeyRef = useRef<string | null>(null);
  const [clearingItem, setClearingItem] = useState<MobileBudgetItemDto | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!canViewTechnical && canViewCommercial) setLineGroup('additional');
  }, [canViewTechnical, canViewCommercial]);

  useEffect(() => {
    const built = buildAdjustmentPayload();
    if (!built.ok) {
      adjustmentSimulationKeyRef.current = null;
      setSimulationPreview(null);
      return;
    }
    const signature = JSON.stringify(built.payload);
    setAdjustmentError(null);
    setSimulationPreview(null);
    const timer = setTimeout(() => {
      adjustmentSimulationKeyRef.current = signature;
      setSimulationPreview(null);
      totals.simulate
        .mutateAsync(built.payload)
        .then((result) => {
          if (adjustmentSimulationKeyRef.current !== signature) return;
          const preview = result as {
            total_sale_price?: number | string;
            sale_price?: number | string;
          };
          const previewTotal = preview.total_sale_price ?? preview.sale_price;
          setSimulationPreview(
            previewTotal === undefined
              ? 'Vista previa calculada sin total de referencia.'
              : `Total con ajuste: ${precision.format('budgets', 'sale_price', Number(previewTotal), currency)}`,
          );
        })
        .catch((error: unknown) => {
          if (adjustmentSimulationKeyRef.current !== signature) return;
          setAdjustmentError(extractErrorMessage(error));
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      adjustmentSimulationKeyRef.current = null;
    };
  }, [adjustmentType, adjustmentMode, adjustmentValue, adjustmentJustification]);

  const catalogQuery = useMobileCatalogQuery(addSearch, lineGroup);
  const actions = contextQuery.data?.actions ?? [];
  const documentRevision = contextQuery.data?.document_revision;
  const activeInstance = (contextQuery.data?.latest_instances ?? []).find(
    (instance) => instance.status === 'pending' || instance.status === 'blocked',
  );
  const workflowActions = useMobileWorkflowActions({
    entityCode: 'budget',
    entityId: budgetId ?? '',
    actions,
    documentRevision,
    activeInstanceId: activeInstance?.id,
    instanceRevision: activeInstance?.revision,
    onDomainCommand: (actionCode, body, revision) =>
      actionMutation.mutateAsync({ action: actionCode, body, revision }),
  });
  const versionsQuery = useMobileBudgetVersionsQuery(
    budgetQuery.data?.budget_code,
    budgetQuery.data?.customer_requirement_id,
  );
  const quotationsQuery = useMobileBudgetQuotationsQuery(
    budgetQuery.data?.customer_requirement_id,
  );
  const hasActiveRequest = activeInstance !== undefined;

  if (budgetQuery.isPending) {
    return (
      <MobileLayout title="Presupuesto" showBack>
        <CardListSkeleton count={3} />
      </MobileLayout>
    );
  }

  if (budgetQuery.isError || !budgetQuery.data) {
    return (
      <MobileLayout title="Presupuesto" showBack>
        <EmptyState
          icon="error"
          title="No se pudo cargar"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void budgetQuery.refetch()}
        />
      </MobileLayout>
    );
  }

  const budget = budgetQuery.data;
  const currency = budget.currency ?? 'USD';
  const clientName = budget.customer_requirement?.client?.legal_name;
  const requirementCode = budget.customer_requirement?.code;
  const items = itemsQuery.data ?? [];
  const versions = versionsQuery.data ?? [];
  const attachments = attachmentsQuery.data ?? [];
  const editable = isBudgetEditable(budget.status) && !hasActiveRequest;
  const editBlockReason = hasActiveRequest
    ? 'Hay una solicitud activa sobre este presupuesto.'
    : !isBudgetEditable(budget.status)
      ? 'El presupuesto ya no admite cambios.'
      : null;
  const existingQuotation =
    (quotationsQuery.data ?? []).find(
      (quotation) =>
        quotation.budget_id === budget.id &&
        (quotation.origin === 'budget' || !quotation.origin) &&
        quotation.status !== 'cancelled' &&
        quotation.status !== 'expired',
    ) ?? null;
  const canGenerateQuotation = budget.status === 'approved' && hasPermission('quotations.create');
  const canSearchCatalog = hasPermission('quotations.create');
  const canSeeItems = canViewTechnical || canViewCommercial;
  const canAddTechnical =
    canViewTechnical && editable && canSearchCatalog && hasPermission('budget_items.create');
  const canEditTechnical =
    canViewTechnical && editable && hasPermission('budget_items.update');
  const canDeleteTechnical =
    canViewTechnical && editable && hasPermission('budget_items.delete');
  const canAddCommercial =
    canViewCommercial && editable && hasPermission('budgets.commercial_items.create');
  const canEditCommercial =
    canViewCommercial && editable && hasPermission('budgets.commercial_items.update');
  const canDeleteCommercial =
    canViewCommercial && editable && hasPermission('budgets.commercial_items.delete');
  const canAdjust =
    canViewCommercial && editable && hasPermission('budgets.commercial_items.create');
  const blindSeller = canViewTechnical && !hasPermission('master_items.create');

  const commercialItems = items.filter((item) => item.line_kind === 'commercial');
  const technicalItems = items.filter((item) => item.line_kind !== 'commercial');
  const adjustmentItems = items.filter((item) => item.adjustment_type);
  const plainCommercial = commercialItems.filter((item) => !item.adjustment_type);
  const plainTechnical = technicalItems.filter((item) => !item.adjustment_type);
  const activeItems = lineGroup === 'sale' ? plainTechnical : plainCommercial;
  const activeLineKind = lineGroup === 'sale' ? 'technical' : 'commercial';
  const canAddActive = lineGroup === 'sale' ? canAddTechnical : canAddCommercial;
  const canEditActive = lineGroup === 'sale' ? canEditTechnical : canEditCommercial;
  const canDeleteActive = lineGroup === 'sale' ? canDeleteTechnical : canDeleteCommercial;
  const activeMasterIds = new Set(
    activeItems.map((item) => item.master_item_id).filter((value): value is string => !!value),
  );
  const catalogResults = (catalogQuery.data ?? []).filter(
    (entry) => !activeMasterIds.has(entry.id),
  );
  const activeSubtotal = activeItems.reduce((sum, item) => {
    const unitPrice = toNumber(item.unit_sale_price);
    return sum + (unitPrice ?? 0) * Number(item.quantity ?? 0);
  }, 0);

  function handleBudgetActionSuccess(actionCode: string, result: unknown) {
    hapticSuccess();
    if (actionCode === 'create_version') {
      const copyId =
        result !== null && typeof result === 'object'
          ? String((result as { id?: unknown }).id ?? '')
          : '';
      toast.success('Nueva versión creada');
      if (copyId) {
        navigate(`/m/budgets/${copyId}`);
        return;
      }
    }
    toast.success('Acción ejecutada correctamente');
    void budgetQuery.refetch();
    void contextQuery.refetch();
  }

  async function handleFiles(files: File[]) {
    if (!budgetId || files.length === 0) return;
    setUploadError(null);
    try {
      await uploadAttachmentsMutation.mutateAsync({ budgetId, files });
      hapticSuccess();
      toast.success(files.length === 1 ? 'Adjunto subido correctamente' : 'Adjuntos subidos correctamente');
    } catch (error) {
      setUploadError(extractErrorMessage(error));
    }
  }

  async function handleGenerateQuotation() {
    try {
      const quotation = await generateMutation.mutateAsync();
      hapticSuccess();
      toast.success(`Cotización ${quotation.quotation_number} creada`);
      void contextQuery.refetch();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  function buildAddPayload(entry: CatalogItemDto): BuildResult {
    const priceValue = Number(entry.price ?? 0) || 0;
    const isGroupable = entry.item_type === 'component' || entry.item_type === 'model';
    const common = {
      master_item_id: entry.id,
      item_type: entry.item_type ?? 'sales',
      quantity: 1,
      master_item_category_id: entry.master_item_category_id ?? null,
      exchange_rate: Number(budget.exchange_rate) || 0,
    };
    if (lineGroup === 'sale') {
      return {
        ok: true,
        payload: {
          ...common,
          line_kind: 'technical',
          ...(isGroupable
            ? { base_price: 0, children: [] as unknown[] }
            : { unit_cost: priceValue, base_price: priceValue }),
        },
      };
    }
    return {
      ok: true,
      payload: {
        ...common,
        item_type: entry.item_type ?? 'sales',
        line_kind: 'commercial',
        ...(isGroupable
          ? { base_price: 0, children: [] as unknown[] }
          : { unit_cost: priceValue, base_price: priceValue }),
      },
    };
  }

  async function handleAddCatalogItem(entry: CatalogItemDto) {
    const built = buildAddPayload(entry);
    if (!built.ok) {
      setAddError(built.error);
      return;
    }
    setAddError(null);
    try {
      await addItemMutation.mutateAsync({ body: built.payload, revision: documentRevision });
      setAddSearch('');
      hapticSuccess();
      toast.success(activeLineKind === 'technical' ? 'Partida agregada' : 'Artículo agregado');
    } catch (error) {
      setAddError(extractErrorMessage(error));
    }
  }

  function startEditing(item: MobileBudgetItemDto) {
    setEditingItemId(item.id);
    setEditPrice(String(Number(item.unit_cost ?? item.base_price ?? 0)));
    setEditMargin(item.margin === null || item.margin === undefined ? '' : String(item.margin));
  }

  async function handleSaveEditing(item: MobileBudgetItemDto) {
    const priceValue = Number(editPrice);
    const marginValue = editMargin.trim() === '' ? null : Number(editMargin);
    if (!Number.isFinite(priceValue) || priceValue < 0) {
      toast.error('Ingresa un precio válido (0 o mayor)');
      return;
    }
    if (countDecimals(editPrice) > precision.decimals('budget_items', 'unit_cost')) {
      toast.error(
        `El precio admite como máximo ${precision.decimals('budget_items', 'unit_cost')} decimales para esta empresa`,
      );
      return;
    }
    if (marginValue !== null && (!Number.isFinite(marginValue) || marginValue < 0 || marginValue > 100)) {
      toast.error('Ingresa un margen válido (0 a 100)');
      return;
    }
    const body: Record<string, unknown> = {};
    if (priceValue !== Number(item.unit_cost ?? item.base_price ?? 0)) body['unit_cost'] = priceValue;
    const currentMargin = item.margin === null || item.margin === undefined ? null : Number(item.margin);
    if (marginValue !== currentMargin) body['margin'] = marginValue;
    if (Object.keys(body).length === 0) {
      setEditingItemId(null);
      toast.success('Sin cambios');
      return;
    }
    try {
      await updateItemMutation.mutateAsync({
        itemId: item.id,
        body,
        revision: documentRevision,
      });
      setEditingItemId(null);
      hapticSuccess();
      toast.success('Partida actualizada');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  async function handleQuantityChange(item: MobileBudgetItemDto, next: number) {
    if (next <= 0) {
      setDeletingItem(item);
      return;
    }
    try {
      await updateItemMutation.mutateAsync({
        itemId: item.id,
        body: { quantity: next },
        revision: documentRevision,
      });
      toast.success('Cantidad actualizada');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  async function handleConfirmDelete() {
    if (!deletingItem) return;
    try {
      await deleteItemMutation.mutateAsync({ itemId: deletingItem.id, revision: documentRevision });
      setDeletingItem(null);
      hapticSuccess();
      toast.success('Partida eliminada');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  function buildAdjustmentPayload(): BuildResult {
    const parsed = Number(adjustmentValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return { ok: false, error: 'El valor debe ser un número mayor a 0' };
    }
    if (adjustmentMode === 'percent' && parsed > 100) {
      return { ok: false, error: 'El porcentaje no puede superar el 100%' };
    }
    if (adjustmentMode === 'amount') {
      if (
        countDecimals(adjustmentValue)
        > precision.decimals('budgets', 'adjustment_amount')
      ) {
        return {
          ok: false,
          error: `El monto admite como maximo ${precision.decimals('budgets', 'adjustment_amount')} decimales para esta empresa`,
        };
      }
    }
    if (!adjustmentJustification || adjustmentJustification.trim().length < 5) {
      return { ok: false, error: 'La justificación es obligatoria (mínimo 5 caracteres)' };
    }
    return {
      ok: true,
      payload: {
        adjustment_type: adjustmentType,
        ...(adjustmentMode === 'percent' ? { percent: parsed } : { amount: parsed }),
        justification: adjustmentJustification.trim(),
      },
    };
  }

  async function handleApplyAdjustment() {
    const built = buildAdjustmentPayload();
    if (!built.ok) {
      setAdjustmentError(built.error);
      return;
    }
    setAdjustmentError(null);
    try {
      await adjustments.applyAdjustment.mutateAsync({ body: built.payload, revision: documentRevision });
      setAdjustmentValue('');
      setAdjustmentJustification('');
      setSimulationPreview(null);
      hapticSuccess();
      toast.success('Ajuste aplicado al precio de venta');
    } catch (error) {
      setAdjustmentError(extractErrorMessage(error));
    }
  }

  async function handleConfirmClearAdjustment() {
    if (!clearingItem) return;
    try {
      await adjustments.clearAdjustment.mutateAsync({ itemId: clearingItem.id, revision: documentRevision });
      setClearingItem(null);
      hapticSuccess();
      toast.success('Ajuste eliminado');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  return (
    <MobileLayout
      title={`${budget.budget_code} · v${budget.version_number}`}
      subtitle={clientName ?? 'Presupuesto'}
      showBack
    >
      <div className="space-y-4">
        <SectionCard
          title="Datos generales"
          action={<StatusBadge status={budget.status} size="sm" />}
        >
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Codigo</dt>
              <dd title={budget.budget_code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                {budget.budget_code}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Version</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                v{budget.version_number}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Cliente</dt>
              <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                {clientName ?? '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Requerimiento</dt>
              <dd title={requirementCode ?? undefined} className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {requirementCode ?? '-'}
              </dd>
            </div>
            {existingQuotation && (
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cotización</dt>
                <dd className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                  {existingQuotation.quotation_number}
                </dd>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Fecha</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(budget.budget_date)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Moneda</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {currency}
              </dd>
            </div>
          </dl>
        </SectionCard>

        <SectionCard title="Importes">
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Subtotal</dt>
              <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                {precision.format('budgets', 'subtotal', toNumber(budget.subtotal), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Ajuste</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {precision.format('budgets', 'adjustment_amount', toNumber(budget.adjustment_amount), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Costo total</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {precision.format('budgets', 'total_cost', toNumber(budget.total_cost), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 font-semibold text-text-primary">Precio de venta</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-base font-bold tabular-nums text-primary">
                {precision.format('budgets', 'sale_price', toNumber(budget.sale_price), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Margen est.</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatPercent(toNumber(budget.estimated_margin))}
              </dd>
            </div>
          </dl>
        </SectionCard>

        {(actions.length > 0 || canGenerateQuotation) && (
          <SectionCard
            title="Acciones"
            subtitle="El servidor habilito estas acciones para este presupuesto."
          >
            <div className="space-y-2">
              <ServerActionRunner
                actions={actions}
                documentRevision={documentRevision}
                onExecute={workflowActions.execute}
                onSuccess={handleBudgetActionSuccess}
                onConflictReload={() => {
                  void budgetQuery.refetch();
                  void contextQuery.refetch();
                }}
              />
              {canGenerateQuotation && !existingQuotation && (
                <MobileButton
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={generateMutation.isPending}
                  icon={<MobileIcon name="request_quote" className="h-5 w-5" />}
                  onClick={() => void handleGenerateQuotation()}
                >
                  Generar cotización
                </MobileButton>
              )}
            </div>
          </SectionCard>
        )}

        {canSeeItems && (
          <SectionCard
            title="Partidas"
            subtitle={editBlockReason ?? 'Edita las partidas técnicas y los artículos de venta.'}
          >
            {itemsQuery.isPending && <CardListSkeleton count={3} />}
            {itemsQuery.isError && (
              <EmptyState
                icon="error"
                title="No se pudieron cargar las partidas"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                onAction={() => void itemsQuery.refetch()}
              />
            )}

            {!itemsQuery.isPending && !itemsQuery.isError && (
              <>
                <div
                  role="radiogroup"
                  aria-label="Grupo de partidas"
                  className="mb-4 inline-flex w-full gap-1 rounded-full bg-bg-tertiary p-1"
                >
                  {canViewTechnical && (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={lineGroup === 'sale'}
                      onClick={() => {
                        setLineGroup('sale');
                        setAddSearch('');
                        setEditingItemId(null);
                      }}
                      className={`flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors ${
                        lineGroup === 'sale'
                          ? 'bg-white text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      Partidas y Modelos
                    </button>
                  )}
                  {canViewCommercial && (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={lineGroup === 'additional'}
                      onClick={() => {
                        setLineGroup('additional');
                        setAddSearch('');
                        setEditingItemId(null);
                      }}
                      className={`flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors ${
                        lineGroup === 'additional'
                          ? 'bg-white text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      Artículos de venta
                    </button>
                  )}
                </div>

                {canAddActive && canSearchCatalog && (
                  <div className="mb-4 space-y-3 border-b border-border-light pb-4">
                    <MobileInput
                      label={lineGroup === 'sale' ? 'Buscar partida o modelo' : 'Buscar artículo de venta'}
                      placeholder="Mínimo 2 letras..."
                      staticLabel
                      value={addSearch}
                      onChange={(e) => setAddSearch(e.target.value)}
                      icon={<MobileIcon name="search" className="h-5 w-5" />}
                    />
                    {addSearch.trim().length >= 2 && (
                      <div className="space-y-2">
                        {catalogQuery.isPending && <CardListSkeleton count={2} />}
                        {catalogQuery.isSuccess && catalogResults.length === 0 && (
                          <p className="text-sm text-text-secondary">Sin resultados para esa búsqueda.</p>
                        )}
                        {catalogResults.slice(0, 10).map((entry) => (
                          <div
                            key={entry.id}
                            className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block break-words text-sm font-semibold text-text-primary">
                                {entry.name}
                              </span>
                              <span className="block truncate text-xs text-text-secondary">
                                {entry.code}
                                {entry.unit_code ? ` · ${entry.unit_code}` : ''}
                                {lineGroup === 'additional' && entry.price !== undefined
                                  ? ` · ${precision.format('quotation_items', 'unit_price', entry.price, entry.currency_code ?? currency)}`
                                  : ''}
                              </span>
                            </span>
                            <button
                              type="button"
                              aria-label={`Agregar ${entry.name}`}
                              disabled={addItemMutation.isPending}
                              onClick={() => void handleAddCatalogItem(entry)}
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white disabled:opacity-40"
                            >
                              <MobileIcon name="add" className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {addError && <p className="break-words text-sm text-error">{addError}</p>}
                  </div>
                )}

                {!canAddActive && editBlockReason && (
                  <p className="mb-3 break-words rounded-xl bg-bg-tertiary px-3 py-2 text-sm text-text-secondary">
                    {editBlockReason}
                  </p>
                )}

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-text-primary">
                    {lineGroup === 'sale' ? 'Partidas y Modelos' : 'Artículos de venta'} ({activeItems.length})
                  </h3>
                  {activeItems.length === 0 && (
                    <div className="rounded-xl bg-bg-tertiary p-4 text-center">
                      <p className="text-sm font-semibold text-text-primary">Sin partidas en este grupo</p>
                    </div>
                  )}
                  <ul className="space-y-2">
                    {activeItems.map((item) => {
                      const salePrice = toNumber(item.unit_sale_price);
                      const cost = !blindSeller ? toNumber(item.unit_cost) : undefined;
                      const reference = salePrice ?? cost;
                      const lineSubtotal = reference !== undefined
                        ? reference * Number(item.quantity ?? 0)
                        : undefined;
                      const referenceField = salePrice !== undefined ? 'unit_sale_price' : 'unit_cost';
                      return (
                        <li key={item.id} className="rounded-xl bg-bg-tertiary px-4 py-3">
                          {editingItemId === item.id ? (
                            <div className="space-y-3">
                              <MobileInput
                                label="Precio base"
                                type="number"
                                inputMode="decimal"
                                staticLabel
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                icon={<MobileIcon name="payments" className="h-5 w-5" />}
                              />
                              <MobileInput
                                label="Margen % (opcional)"
                                type="number"
                                inputMode="decimal"
                                staticLabel
                                value={editMargin}
                                onChange={(e) => setEditMargin(e.target.value)}
                                icon={<MobileIcon name="trending_up" className="h-5 w-5" />}
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <MobileButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingItemId(null)}
                                >
                                  Cancelar
                                </MobileButton>
                                <MobileButton
                                  variant="primary"
                                  size="sm"
                                  loading={updateItemMutation.isPending}
                                  onClick={() => void handleSaveEditing(item)}
                                >
                                  Guardar
                                </MobileButton>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <span className="min-w-0 flex-1">
                                <span className="line-clamp-2 break-words text-sm font-semibold text-text-primary">
                                  {itemTitle(item)}
                                </span>
                                <span className="mt-1 block truncate text-xs tabular-nums text-text-secondary">
                                  {reference !== undefined
                                    ? `${precision.format('budget_items', referenceField, reference, currency)} · Cant. ${String(item.quantity)}`
                                    : `Cant. ${String(item.quantity)}`}
                                </span>
                                {lineSubtotal !== undefined && (
                                  <span className="mt-0.5 block truncate text-xs font-medium tabular-nums text-text-secondary">
                                    Subtotal: {precision.format('budget_items', referenceField, lineSubtotal, currency)}
                                  </span>
                                )}
                              </span>
                              <span className="flex shrink-0 items-center gap-1">
                                {canEditActive ? (
                                  <QuantityStepper
                                    label={itemTitle(item)}
                                    value={Number(item.quantity ?? 1)}
                                    onChange={(next) => handleQuantityChange(item, next)}
                                    compact
                                  />
                                ) : (
                                  <span className="text-xs tabular-nums text-text-secondary">
                                    Cant. {String(item.quantity)}
                                  </span>
                                )}
                                {lineGroup === 'additional' && canEditActive && (
                                  <button
                                    type="button"
                                    aria-label={`Editar partida ${itemTitle(item)}`}
                                    onClick={() => startEditing(item)}
                                    className="flex h-11 w-11 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light"
                                  >
                                    <MobileIcon name="edit_note" className="h-5 w-5" />
                                  </button>
                                )}
                                {canDeleteActive && (
                                  <button
                                    type="button"
                                    aria-label={`Eliminar partida ${itemTitle(item)}`}
                                    onClick={() => setDeletingItem(item)}
                                    className="flex h-11 w-11 items-center justify-center rounded-full text-error transition-colors hover:bg-error-light"
                                  >
                                    <MobileIcon name="cancel" className="h-5 w-5" />
                                  </button>
                                )}
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex items-center justify-between gap-2 border-t border-border-light pt-3">
                    <span className="text-sm font-medium text-text-secondary">
                      Subtotal {lineGroup === 'sale' ? 'Partidas y Modelos' : 'Artículos de venta'}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">
                      {precision.format(
                        'budget_items',
                        activeItems.some((item) => toNumber(item.unit_sale_price) !== undefined)
                          ? 'unit_sale_price'
                          : 'unit_cost',
                        activeSubtotal,
                        currency,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-2">
                    <span className="font-semibold text-text-primary">Total general</span>
                    <span className="shrink-0 text-base font-bold tabular-nums text-primary">
                      {precision.format(
                        'budgets',
                        'sale_price',
                        toNumber(budget.sale_price),
                        currency,
                      )}
                    </span>
                  </div>
                </div>
              </>
            )}
          </SectionCard>
        )}

        {canAdjust && (
          <SectionCard title="Ajuste global" subtitle={editBlockReason ?? undefined}>
              <div className="space-y-3">
                <div role="radiogroup" aria-label="Tipo de ajuste" className="flex gap-1 rounded-full bg-bg-tertiary p-1">
                  {(
                    [
                      { value: 'discount', label: 'Descuento' },
                      { value: 'surcharge', label: 'Recargo' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={adjustmentType === option.value}
                      onClick={() => setAdjustmentType(option.value)}
                      className={`flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold ${
                        adjustmentType === option.value
                          ? 'bg-white text-text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div role="radiogroup" aria-label="Forma del ajuste" className="flex gap-1 rounded-full bg-bg-tertiary p-1">
                  {(
                    [
                      { value: 'amount', label: 'Monto fijo' },
                      { value: 'percent', label: 'Porcentaje' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={adjustmentMode === option.value}
                      onClick={() => setAdjustmentMode(option.value)}
                      className={`flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold ${
                        adjustmentMode === option.value
                          ? 'bg-white text-text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <MobileInput
                  label={adjustmentMode === 'percent' ? 'Porcentaje (0 a 100)' : 'Monto'}
                  type="number"
                  inputMode="decimal"
                  staticLabel
                  isRequired
                  value={adjustmentValue}
                  onChange={(e) => setAdjustmentValue(e.target.value)}
                  icon={<MobileIcon name="payments" className="h-5 w-5" />}
                />
                <MobileInput
                  label="Justificación"
                  placeholder="Mínimo 5 caracteres..."
                  staticLabel
                  isRequired
                  value={adjustmentJustification}
                  onChange={(e) => setAdjustmentJustification(e.target.value)}
                  icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
                />
                {adjustmentError && <p className="break-words text-sm text-error">{adjustmentError}</p>}
                {totals.simulate.isPending && (
                  <p role="status" className="break-words rounded-xl bg-bg-tertiary px-4 py-3 text-sm text-text-secondary">
                    Calculando vista previa…
                  </p>
                )}
                {simulationPreview && !totals.simulate.isPending && (
                  <p role="status" className="break-words rounded-xl bg-info-light px-4 py-3 text-sm text-info-dark">
                    {simulationPreview}
                  </p>
                )}
                <MobileButton
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={totals.simulate.isPending}
                  loading={adjustments.applyAdjustment.isPending}
                  onClick={() => void handleApplyAdjustment()}
                >
                  Aplicar ajuste
                </MobileButton>
                {adjustmentItems.length > 0 && (
                  <ul className="space-y-2 pt-1">
                    {adjustmentItems.map((adjustment) => (
                      <li
                        key={adjustment.id}
                        className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-text-primary">
                            {adjustment.adjustment_type === 'surcharge' ? 'Recargo' : 'Descuento'}
                          </span>
                          <span className="block break-words text-xs text-text-secondary line-clamp-2">
                            {adjustment.adjustment_justification ?? 'Sin justificación'}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Quitar ajuste ${adjustment.adjustment_type ?? ''}`}
                          onClick={() => setClearingItem(adjustment)}
                          className="flex h-11 w-11 items-center justify-center rounded-full text-error transition-colors hover:bg-error-light"
                        >
                          <MobileIcon name="cancel" className="h-5 w-5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
          </SectionCard>
        )}

        <SectionCard title={`Adjuntos (${attachments.length})`}>
          {attachmentsQuery.isPending && <CardListSkeleton count={2} />}
          {attachmentsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar los adjuntos"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void attachmentsQuery.refetch()}
            />
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length === 0 && (
            <div className="mb-3 rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin adjuntos</p>
              <p className="text-sm text-text-secondary">Agrega fotos o archivos del presupuesto.</p>
            </div>
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length > 0 && (
            <ul className="mb-3 space-y-2">
              {attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="folder_open" className="h-5 w-5 shrink-0 text-text-tertiary" />
                  <span className="min-w-0 flex-1">
                    <span title={attachment.file_name} className="block truncate text-sm font-semibold text-text-primary">
                      {attachment.file_name}
                    </span>
                    {attachment.description && (
                      <span className="block break-words text-xs text-text-secondary line-clamp-2">
                        {attachment.description}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <FileCapture onFiles={(files) => void handleFiles(files)} disabled={uploadAttachmentsMutation.isPending} />
          {uploadAttachmentsMutation.isPending && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-info-light px-4 py-3">
              <MobileIcon name="sync" className="h-5 w-5 shrink-0 animate-spin text-info-dark" />
              <p className="min-w-0 flex-1 truncate text-sm text-info-dark">Subiendo adjuntos...</p>
            </div>
          )}
          {uploadError && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
              <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
              <p className="min-w-0 flex-1 break-words text-sm text-error">{uploadError}</p>
            </div>
          )}
        </SectionCard>

        <SectionCard title={`Versiones (${versions.length})`}>
          {versionsQuery.isPending && <CardListSkeleton count={2} />}
          {versionsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar las versiones"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void versionsQuery.refetch()}
            />
          )}
          {!versionsQuery.isPending && !versionsQuery.isError && versions.length === 0 && (
            <div className="rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin versiones</p>
            </div>
          )}
          {!versionsQuery.isPending && !versionsQuery.isError && versions.length > 0 && (
            <ul className="space-y-2">
              {versions.map((version) => (
                <li
                  key={version.id}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="receipt_long" className="h-5 w-5 shrink-0 text-text-tertiary" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-text-primary">
                      v{version.version_number} · {version.status}
                    </span>
                    <span className="block truncate text-xs text-text-secondary">
                      {formatDate(version.budget_date)} ·{' '}
                      {precision.format('budgets', 'sale_price', Number(version.sale_price), version.currency ?? currency)}
                    </span>
                  </span>
                  {version.id !== budget.id ? (
                    <Link
                      to={`/m/budgets/${version.id}`}
                      className="flex min-h-[44px] shrink-0 items-center rounded-full bg-secondary-light px-4 text-sm font-semibold text-secondary-dark-text"
                    >
                      Ver ficha
                    </Link>
                  ) : (
                    <span className="shrink-0 text-xs text-text-tertiary">Actual</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <StatusHistoryCard
          entityType="budget"
          entityId={budgetId}
          enabled={hasPermission('budgets.view')}
        />

      </div>

      {deletingItem && (
        <ConfirmDialog
          title="Eliminar partida"
          message={`¿Confirmas eliminar "${itemTitle(deletingItem)}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          busy={deleteItemMutation.isPending}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setDeletingItem(null)}
        />
      )}

      {clearingItem && (
        <ConfirmDialog
          title="Quitar ajuste"
          message="¿Confirmas quitar este ajuste del precio de venta?"
          confirmLabel="Quitar"
          danger
          busy={adjustments.clearAdjustment.isPending}
          onConfirm={() => void handleConfirmClearAdjustment()}
          onCancel={() => setClearingItem(null)}
        />
      )}
    </MobileLayout>
  );
}
